import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, IconButton,
  Stack, CircularProgress
} from "@mui/material";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer
} from "recharts";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import EditIcon from "@mui/icons-material/Edit";
import ControlCameraIcon from "@mui/icons-material/ControlCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FlagIcon from "@mui/icons-material/Flag";
import { togglePortfolioFlag } from "../../../../services/portfolioService";
import { Tooltip } from "@mui/material";
import { useAuth, ROLES } from "../../../../components/auth/AuthProvider";
import { HistoryOutlined } from "@mui/icons-material";
import { useHistory } from "../../../../context/HistoryContext";

// Static Chart Data
const chartData = [
  { month: "Jan", current: 180, realised: 25 },
  { month: "Feb", current: 190, realised: 28 },
  { month: "Mar", current: 210, realised: 32 },
  { month: "Apr", current: 230, realised: 30 },
  { month: "May", current: 220, realised: 29 },
  { month: "Jun", current: 215, realised: 28 },
  { month: "Jul", current: 220, realised: 29 },
  { month: "Aug", current: 240, realised: 30 },
  { month: "Sep", current: 235, realised: 29 },
  { month: "Oct", current: 250, realised: 30 },
  { month: "Nov", current: 240, realised: 29 },
  { month: "Dec", current: 235, realised: 30.1 },
];

const InfoRow = ({ label, value }) => (
  <Box display="flex" alignItems="flex-start" mb={1.5} gap={1}>
    <Typography
      sx={{
        fontSize: { xs: 11, sm: 12 },
        fontWeight: 500,
        color: "#6B7280",
        minWidth: { xs: "110px", sm: "140px" },
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: 11, sm: 12 },
        fontWeight: 600,
        color: "#111827",
        textAlign: "left",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const StatCard = ({ label, value, tooltip, showFlag }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.5, sm: 2 },
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      backgroundColor: "#FFFFFF",
      position: "relative",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    {/* Top Row: Label and Icons */}
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography fontSize={11} color="#6B7280">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <HelpOutlineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
          </Tooltip>
        )}
      </Box>
      {showFlag && <FlagIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />}
    </Box>

    {/* Value Row */}
    <Typography
      sx={{
        fontSize: { xs: 20, sm: 22, md: 24 },
        fontWeight: 700,
        color: "#111827",
        fontFamily: "serif",
      }}
    >
      {value}
    </Typography>
  </Paper>
);

export default function Overview() {
  const { user, role } = useAuth();
  const isPartnerUser = role === ROLES.PARTNER;
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId } = useParams();
  const { openHistory } = useHistory();

  const dynamicPartner_doc = location.state?.partner_doc;
  const dynamicKpi = location.state?.kpi || "CARR";
  const dynamicPartner_name = location.state?.partner_name;
  const dynamicDealId = location.state?.deal_id;
  const dynamicArId = location.state?.ar_id;

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFlagged, setIsFlagged] = useState(false);
  const [flagLoading, setFlagLoading] = useState(false);

  useEffect(() => {
    if (apiData) {
      setIsFlagged(apiData.flag === true || apiData.flag === "true");
    }
  }, [apiData]);

  const handleFlagToggle = async () => {
    if (!isPartnerUser) return;
    if (!dynamicArId) {
      console.error("AR_Id not available");
      return;
    }
    setFlagLoading(true);
    const newVal = !isFlagged;
    try {
      await togglePortfolioFlag(dynamicArId, newVal, user?.email, decodeURIComponent(companyId));
      setIsFlagged(newVal);
    } catch {
      console.error("Failed to toggle flag");
    } finally {
      setFlagLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const companyName = decodeURIComponent(companyId);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4200/api";
        const url = `${API_BASE_URL}/portfolio/overview?dealId=${dynamicDealId}&partnerCode=${dynamicPartner_doc}&partnerName=${dynamicPartner_name}&company=${companyName}&kpi=${dynamicKpi}`;

        const response = await fetch(url);
        const result = await response.json();

        if (result.status === "success" && result.data) {
          setApiData(result.data);
        } else {
          setApiData(null);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) fetchData();
  }, [companyId, dynamicPartner_doc, dynamicPartner_name, dynamicKpi]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  if (!apiData)
    return (
      <Box p={4}>
        <Typography>No data found for "{companyId}" with Partner.</Typography>
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, sm: 2.5, md: 3 },
          maxWidth: 1600,
          margin: "0 auto",
          alignItems: "flex-start",
        }}
      >
        {/* ── LEFT COLUMN — fixed width, never grows ── */}
        {/* <Box sx={{ flex: "0 0 400px", width: "400px", minWidth: 0 }}> */}
        <Box sx={{ flex: "0 0 auto", width: { xs: "100%", md: "400px" }, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, sm: 2.5 }, border: "1px solid #E5E7EB", borderRadius: "12px", mb: 2 }}
          >
            <InfoRow label="Company" value={apiData.Company} />
            <InfoRow label="Partner" value={apiData.partner} />
            <InfoRow label="Sector" value={apiData.company_sector} />
            <InfoRow label="Location" value={apiData.company_city} />
            <InfoRow label="Fund Family" value={apiData.fund_family} />
            <InfoRow label="Reporting Currency" value={apiData.Reporting_Currency} />
            <InfoRow label="Board Representative" value={apiData.board_representative} />
            <InfoRow label="Top-line KPI" value={apiData.Top_line_KPI} />
            <InfoRow label="Vintage Year" value={apiData.Vintage_Year} />
           {/*  <InfoRow label="Website" value={apiData.website} /> */}
            <InfoRow
              label="Website"
              value={
                <span
                  onClick={() => {
                    if (!apiData.website) return;
                    const url = apiData.website.startsWith("http")
                      ? apiData.website
                      : "https://" + apiData.website;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  style={{
                    color: "#1976d2",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {apiData.website || "—"}
                </span>
              }
            />
            <InfoRow label="Description" value={apiData.company_description} />
          </Paper>

          <Stack spacing={2} sx={{ width: "100%" }}>
            <StatCard
              label="Grade"
              value={apiData.grade}
              tooltip="Performance grade based on financial health."

            />

            <Paper
              elevation={0}
              sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid #E5E7EB", borderRadius: "12px", backgroundColor: "#FFFFFF" }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography fontSize={11} color="#6B7280">Flag for Century Team</Typography>
                  <Tooltip title="Flag this company for Century Team attention" arrow placement="top">
                    <HelpOutlineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
                  </Tooltip>
                </Box>

                {isPartnerUser ? (
                  <Tooltip title={isFlagged ? "Remove flag" : "Flag for Century Team"} arrow>
                    <IconButton
                      onClick={handleFlagToggle}
                      disabled={flagLoading}
                      size="small"
                      sx={{ transition: "all 0.2s ease", "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" } }}
                    >
                      {flagLoading ? (
                        <CircularProgress size={18} sx={{ color: isFlagged ? "#1976d2" : "#9CA3AF" }} />
                      ) : (
                        <FlagIcon sx={{ fontSize: 20, color: isFlagged ? "#1976d2" : "#9CA3AF", transition: "color 0.2s ease, transform 0.2s ease", transform: isFlagged ? "scale(1.15)" : "scale(1)" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={isFlagged ? "Flagged for Century Team" : "Not flagged"} arrow>
                    <FlagIcon sx={{ fontSize: 20, color: isFlagged ? "#1976d2" : "#9CA3AF", mr: 1 }} />
                  </Tooltip>
                )}
              </Box>
              <Typography fontSize={13} fontWeight={600} color="#111827" mt={1}>
                {isFlagged ? "Flagged" : "Not flagged"}
              </Typography>
            </Paper>
          </Stack>
        </Box>

        {/* ── RIGHT COLUMN — grows to fill ALL remaining space ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Investment section */}
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, sm: 2.5, md: 3 }, border: "1px solid #E5E7EB", borderRadius: "12px", mb: 2, bgcolor: "#FAFAFA" }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <ShowChartIcon sx={{ width: 12, height: 12, bgcolor: "#d4b89649", borderRadius: "2px", p: 0.4 }} />
              <Typography fontSize={14} fontWeight={700}>Investment</Typography>
            </Box>

            {/* 3 cards — flex:1 with minWidth so they wrap gracefully at high zoom */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "stretch" }}>
              <Box sx={{ flex: "1 1 140px", minWidth: "140px", display: "flex" }}>
                <StatCard label="% Ownership" value={apiData.Ownership} />
              </Box>
              <Box sx={{ flex: "1 1 140px", minWidth: "140px", display: "flex" }}>
                <StatCard label="Invested Capital" value={`$${apiData.invested_capital_in_million_usd.toFixed(2)}M`} />
              </Box>
              <Box sx={{ flex: "1 1 140px", minWidth: "140px", display: "flex" }}>
                <StatCard label="Total Capital Budget" value={`$${apiData.total_capital_budget.toFixed(2)}M`} />
              </Box>
            </Box>
          </Paper>

          {/* Value section */}
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, sm: 2.5, md: 3 }, border: "1px solid #E5E7EB", borderRadius: "12px", bgcolor: "#FAFAFA" }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <ShowChartIcon sx={{ width: 12, height: 12, bgcolor: "#d4b89649", borderRadius: "2px", p: 0.4 }} />
              <Typography fontSize={14} fontWeight={700}>Value</Typography>
            </Box>

            {/* 2 cards — flex:1 with minWidth so they wrap gracefully at high zoom */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "stretch" }}>
              <Box sx={{ flex: "1 1 140px", minWidth: "140px", display: "flex" }}>
                <StatCard label="Current Value" value={`$${apiData.current_value.toFixed(2)}M`} />
              </Box>
              <Box sx={{ flex: "1 1 140px", minWidth: "140px", display: "flex" }}>
                <StatCard label="Realized Value" value={`$${apiData.realized_value.toFixed(2)}M`} />
              </Box>
            </Box>
          </Paper>
        </Box>

      </Box>
    </Box>
  );
}